import { formatToCNPJ, formatToCPF } from "@utils/formatters";
import Modal from "components/modal/modal";
import styled from "styled-components";
import { cUFList, CustomTNFeInfNFeIDENFref, defaultCustomNFref, getNfRefTypeItem, getNfRefTypeKey, refTypes } from "../tabs/helper/nfeRefTabHelper";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import Picklist, { PickListItem } from "@components/picklist/picklist";
import { ModEnum, RefECFMod, RefNFPMod } from "@models/TNFe";
import { toast } from "react-toastify";

const ItemModalFrame = styled.div`
        top: 6px;
        left: -14px;
        width: 100%;
        position: absolute;
        z-index: 10000;

        .modal-body {
            padding: 15px;
            overflow: scroll;
        }

        .first-picklist #picklist {
            z-index: 4 !important;
        }

        .min-from {
            min-height: 320px !important;
        }
`;

interface ItemModalProps {
    onSave: (ref: CustomTNFeInfNFeIDENFref) => void
    handleClose: Function
    nFref: CustomTNFeInfNFeIDENFref
}

export function NfRefModal({ onSave, handleClose, nFref }: ItemModalProps) {

    const { handleSubmit, register, control, setValue, watch, reset, formState: { errors } } = useForm<CustomTNFeInfNFeIDENFref>({ defaultValues: defaultCustomNFref, shouldUnregister: true });

    const nfType = watch("nfType");

    function enumToPickListItems<T extends Record<string, string>>(e: T): PickListItem[] {
        return Object.entries(e).map(([codigo, valor], index) => ({ id: index, codigo: String(codigo), value: String(valor) }));
    }

    const refNFModList = enumToPickListItems(ModEnum);
    const refNFPModList = enumToPickListItems(RefNFPMod);
    const refECFModList = enumToPickListItems(RefECFMod);

    function validarCampos(obj: { [key: string]: any }): boolean {
        const keys = Object.keys(obj).filter(key => key !== 'id' && key !== "nfType");
        const algumPreenchido = keys.some(key => {
            const value = obj[key];
            return value !== null && value !== undefined && value !== '';
        });

        return algumPreenchido;
    }

    function onSubmit(form: CustomTNFeInfNFeIDENFref) {
        let valid = false;
        if (form.refECF && nfType?.codigo === 'refECF') {
            valid = validarCampos(form.refECF);
        } else if (form.refNF && nfType?.codigo === 'refNF') {
            valid = validarCampos(form.refNF);
        } else if (form.refNFP && nfType?.codigo === 'refNFP') {
            valid = validarCampos(form.refNFP);
        } else {
            valid = validarCampos(form);
        }

        if(!valid) {
            toast.warn("Não foi possível salvar, nenhum campo preenchdio.");
            return;
        }

        onSave(form);
        handleClose();
    }

    const actionButtons = [{
        text: 'Cancelar',
        actionCallback: () => handleClose(),
        positiveAction: false
    },
    {
        text: 'Confirmar',
        actionCallback: () => handleSubmit(onSubmit, handleError)(),
        positiveAction: true
    }]

    function handleError() {
        if (errors?.refCTe?.message) {
            toast.warn(errors?.refCTe?.message);
            return;
        }
        if (errors?.refNFe?.message) {
            toast.warn(errors?.refNFe?.message);
            return;
        }
        if (errors?.refNFeSig?.message) {
            toast.warn(errors?.refNFeSig?.message);
            return;
        }
        if (errors?.refECF) {
            { errors?.refECF.ncoo && toast.warn(errors?.refECF?.ncoo?.message) }
            { errors?.refECF.necf && toast.warn(errors?.refECF?.necf?.message) }
            return;
        }
        if (errors?.refNF) {
            { errors?.refNF.aamm && toast.warn(errors?.refNF?.aamm?.message) }
            { errors?.refNF.cnpj && toast.warn(errors?.refNF?.cnpj?.message) }
            return;
        }
        if (errors?.refNFP) {
            { errors?.refNFP.aamm && toast.warn(errors?.refNFP.aamm?.message) }
            { errors?.refNFP.cnpj && toast.warn(errors?.refNFP.cnpj?.message) }
            { errors?.refNFP.cpf && toast.warn(errors?.refNFP.cpf?.message) }
            return;
        }
    }

    useEffect(() => {
        if (nFref) {
            const key = getNfRefTypeKey(nFref);
            if (key === "refNFP") {
                reset({ ...nFref, nfType: getNfRefTypeItem(nFref), refNFP: { ...nFref.refNFP, cpf: formatToCPF(nFref.refNFP?.cpf || ''), cnpj: formatToCNPJ(nFref.refNFP?.cnpj || '') } });
            } else if (key === "refNF") {
                reset({ ...nFref, nfType: getNfRefTypeItem(nFref), refNF: { ...nFref.refNF, cnpj: formatToCNPJ(nFref.refNFP?.cnpj || '') } });
            } else {
                reset({ ...nFref, nfType: getNfRefTypeItem(nFref) });
            }
        }
    }, [nFref]);

    return (
        <ItemModalFrame>
            <Modal headerTitle={nFref?.id ? "Editar Referência" : "Criar Referência"} closeOnClickAway={true} handleClose={handleClose} loading={false} actionButtons={actionButtons}>
                <div className="edit-frame">
                    <aside className={['bottom-frame', 'edit-border', 'visible'].join(' ')}>
                        <div style={{ minHeight: 180 }} className={`content ${nfType?.codigo === "refNFP" || nfType?.codigo === "refNF" ? "min-from" : ""}`}>
                            <form style={{ minWidth: 500 }} key={nFref?.id}>
                                <input type="hidden" {...register("id")} />

                                <Controller name="nfType" defaultValue={getNfRefTypeItem(nFref)} control={control} rules={{ required: true }} render={({ field }) =>
                                    <div className="first-picklist" style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                        <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Tipo de Documento</span>
                                        <Picklist
                                            items={refTypes}
                                            placeholder="Selecione"
                                            selected={field.value ? [field.value] : []}
                                            showEndMessage={false}
                                            allowDeselection={false}
                                            showSearch={false}
                                            itemKey={"nfType"}
                                            handleApplySelection={(values) => field.onChange(values[0])}
                                        />
                                    </div>
                                } />
                                {nfType?.codigo === "refNFe" &&
                                    <div style={{ display: 'flex', width: '100%', marginTop: 10, marginBottom: 10 }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                            <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Chave NF-e</span>
                                            <input type="text" {...register("refNFe", {
                                                maxLength: { value: 44, message: 'A Chave NF-e deve ser composta por 44 dígitos' },
                                                pattern: {
                                                    value: /^\d+$/,
                                                    message: 'Apenas numeros são permitidos para Chave NF-e',
                                                }
                                            })} />
                                        </div>
                                    </div>
                                }
                                {nfType?.codigo === "refNFeSig" &&
                                    <div style={{ display: 'flex', width: '100%', marginTop: 10, marginBottom: 10 }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                            <span className="input-label" style={{ whiteSpace: 'nowrap' }}>NF-e (com assinatura)</span>
                                            <input type="text" {...register("refNFeSig", {
                                                maxLength: { value: 44, message: 'A Chave NF-e (com assinatura) deve ser composta por 44 dígitos' },
                                                pattern: {
                                                    value: /^\d+$/,
                                                    message: 'Apenas numeros são permitidos para Chave NF-e (com assinatura)',
                                                }
                                            })} />
                                        </div>
                                    </div>
                                }
                                {nfType?.codigo === "refCTe" &&
                                    <div style={{ display: 'flex', width: '100%', marginTop: 10, marginBottom: 10 }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                            <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Chave CT-e</span>
                                            <input type="text" {...register("refCTe", {
                                                maxLength: { value: 44, message: 'A Chave CT-e deve ser composta por 44 dígitos' },
                                                pattern: {
                                                    value: /^\d+$/,
                                                    message: 'Apenas numeros são permitidos para Chave CT-e',
                                                }
                                            })} />
                                        </div>
                                    </div>
                                }
                                {nfType?.codigo === "refECF" &&
                                    <>
                                        <div style={{ display: 'flex', width: '100%', marginTop: 10, marginBottom: 10 }}>
                                            <Controller name="refECF.mod" control={control} render={({ field }) =>
                                                <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                                    <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Código do modelo do Documento Fiscal</span>
                                                    <Picklist
                                                        items={refECFModList}
                                                        placeholder="Selecione"
                                                        selected={[refECFModList.find(item => item.value === field.value)].filter(Boolean) as PickListItem[]}
                                                        showEndMessage={false}
                                                        allowDeselection={false}
                                                        showSearch={false}
                                                        itemKey={"refCTeMod"}
                                                        handleApplySelection={(values: PickListItem[]) => field.onChange(values.length > 0 ? values[0].value : '')}
                                                    />
                                                </div>
                                            } />
                                        </div>
                                        <div style={{ display: 'flex', width: '100%', marginTop: 10, marginBottom: 10 }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                                <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Número do Contador de Ordem de Operação</span>
                                                <input type="text" {...register("refECF.ncoo", {
                                                    pattern: {
                                                        value: /^[0-9]{1,6}$/,
                                                        message: 'Número do Contador de Ordem de Operação deve ser entre 1 e 6 dígitos numéricos',
                                                    },
                                                })} />
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', width: '100%', marginTop: 10, marginBottom: 10 }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                                <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Número de ordem sequencial do ECF</span>
                                                <input type="text" {...register("refECF.necf", {
                                                    pattern: {
                                                        value: /^[0-9]{1,3}$/,
                                                        message: 'Número de ordem sequencial do ECF deve ser entre 1 e 3 dígitos numéricos',
                                                    },
                                                })} />
                                            </div>
                                        </div>
                                    </>
                                }
                                {nfType?.codigo === "refNF" &&
                                    <>
                                        <div style={{ display: 'flex', width: '100%', marginTop: 10, marginBottom: 10 }}>
                                            <Controller name="refNF.mod" control={control} render={({ field }) =>
                                                <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '50%' }}>
                                                    <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Código do modelo do Documento Fiscal</span>
                                                    <Picklist
                                                        items={refNFModList}
                                                        placeholder="Selecione"
                                                        selected={[refNFModList.find(item => item.value === field.value)].filter(Boolean) as PickListItem[]}
                                                        showEndMessage={false}
                                                        allowDeselection={false}
                                                        showSearch={false}
                                                        itemKey={"refNFMod"}
                                                        handleApplySelection={(values: PickListItem[]) => field.onChange(values.length > 0 ? values[0].value : '')}
                                                    />
                                                </div>
                                            } />
                                            <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                                <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Número do Documento Fiscal</span>
                                                <input type="text" {...register("refNF.nnf")} />
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                                <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Série</span>
                                                <input type="text" {...register("refNF.serie")} />
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', width: '100%', marginTop: 10, marginBottom: 10 }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                                <span className="input-label" style={{ whiteSpace: 'nowrap' }}>CNPJ</span>
                                                <input type="text" {...register("refNF.cnpj", {
                                                    pattern: {
                                                        value: /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/,
                                                        message: 'Formato CPNPJ inválido. Use apenas dígitos numéricos no formato 00.000.000/0000-00',
                                                    },
                                                })} maxLength={18} onChange={(e) => setValue("refNF.cnpj", formatToCNPJ(e.target.value))} />
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                                <span className="input-label" style={{ whiteSpace: 'nowrap' }}>AAMM da emissão da NF</span>
                                                <input type="text" {...register("refNF.aamm", {
                                                    pattern: {
                                                        value: /^([0-9]{2}0[1-9]|[0-9]{2}1[0-2])$/,
                                                        message: 'AAMM da emissão deve ser um formato válido (meses válidos de 01 a 12)',
                                                    },
                                                })} />
                                            </div>

                                            <Controller name="refNF.cuf" control={control} render={({ field }) =>
                                                <div style={{ display: 'flex', flexDirection: 'column', width: '50%' }}>
                                                    <span className="input-label" style={{ whiteSpace: 'nowrap' }}>UF do emitente</span>
                                                    <Picklist
                                                        items={cUFList}
                                                        placeholder="Selecione"
                                                        selected={[cUFList.find(item => item.codigo === field.value)].filter(Boolean) as PickListItem[]}
                                                        showEndMessage={false}
                                                        allowDeselection={false}
                                                        showSearch={false}
                                                        itemKey={"refNFCuf"}
                                                        handleApplySelection={(values: PickListItem[]) => field.onChange(values.length > 0 ? values[0].codigo : '')}
                                                    />
                                                </div>
                                            } />
                                        </div>
                                    </>
                                }
                                {nfType?.codigo === "refNFP" &&
                                    <>
                                        <div style={{ display: 'flex', width: '100%', marginTop: 10, marginBottom: 10 }}>
                                            <Controller name="refNFP.mod" control={control} render={({ field }) =>
                                                <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '50%' }}>
                                                    <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Código do modelo do Documento Fiscal</span>
                                                    <Picklist
                                                        items={refNFPModList}
                                                        placeholder="Selecione"
                                                        selected={[refNFPModList.find(item => item.value === field.value)].filter(Boolean) as PickListItem[]}
                                                        showEndMessage={false}
                                                        allowDeselection={false}
                                                        showSearch={false}
                                                        itemKey={"refNFPMod"}
                                                        handleApplySelection={(values: PickListItem[]) => field.onChange(values.length > 0 ? values[0].value : '')}
                                                    />
                                                </div>
                                            } />
                                            <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                                <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Número do Documento Fiscal</span>
                                                <input type="text" {...register("refNFP.nnf")} />
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                                <span className="input-label" style={{ whiteSpace: 'nowrap' }}>Série</span>
                                                <input type="text" {...register("refNFP.serie")} />
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', width: '100%', marginTop: 10, marginBottom: 10 }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                                <span className="input-label" style={{ whiteSpace: 'nowrap' }}>AAMM da emissão da NF de produtor</span>
                                                <input type="text" {...register("refNFP.aamm", {
                                                    pattern: {
                                                        value: /^([0-9]{2}0[1-9]|[0-9]{2}1[0-2])$/,
                                                        message: 'AAMM da emissão da NF de produtor deve ser um formato válido (meses válidos de 01 a 12)',
                                                    },
                                                })} />
                                            </div>
                                            <Controller name="refNFP.cuf" control={control} render={({ field }) =>
                                                <div style={{ display: 'flex', flexDirection: 'column', width: '50%' }}>
                                                    <span className="input-label" style={{ whiteSpace: 'nowrap' }}>UF do emitente</span>
                                                    <Picklist
                                                        items={cUFList}
                                                        placeholder="Selecione"
                                                        selected={[cUFList.find(item => item.codigo === field.value)].filter(Boolean) as PickListItem[]}
                                                        showEndMessage={false}
                                                        allowDeselection={false}
                                                        showSearch={false}
                                                        itemKey={"refNFPCuf"}
                                                        handleApplySelection={(values: PickListItem[]) => field.onChange(values.length > 0 ? values[0].codigo : '')}
                                                    />
                                                </div>
                                            } />
                                        </div>
                                        <div style={{ display: 'flex', width: '100%', marginTop: 10, marginBottom: 10 }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                                <span className="input-label" style={{ whiteSpace: 'nowrap' }}>CNPJ</span>
                                                <input type="text" {...register("refNFP.cnpj", {
                                                    pattern: {
                                                        value: /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/,
                                                        message: 'Formato CPNPJ inválido. Use apenas dígitos numéricos no formato 00.000.000/0000-00',
                                                    },
                                                })} maxLength={18} onChange={(e) => setValue("refNFP.cnpj", formatToCNPJ(e.target.value))} />
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                                <span className="input-label" style={{ whiteSpace: 'nowrap' }}>CPF</span>
                                                <input type="text" {...register("refNFP.cpf", {
                                                    pattern: {
                                                        value: /^\d{3}\.\d{3}\.\d{3}-\d{2}$/,
                                                        message: 'Formato CPF inválido. Use apenas dígitos numéricos no formato 000.000.000-00',
                                                    },
                                                })} maxLength={14} onChange={(e) => setValue("refNFP.cpf", formatToCPF(e.target.value))} />
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', marginRight: '15px', width: '100%' }}>
                                                <span className="input-label" style={{ whiteSpace: 'nowrap' }}>IE do emitente da NF de Produtor</span>
                                                <input type="text" {...register("refNFP.ie")} />
                                            </div>
                                        </div>
                                    </>
                                }
                            </form>
                        </div>
                    </aside>
                </div>
            </Modal>
        </ItemModalFrame>
    );
}